"use client";

import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { patch, fetchData } from "@/utils/api-handler";
import { toast } from "sonner";
import { FormField } from "@/components/shared/form-fields";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TEXTAREA_MIN_HEIGHT } from "@/utils/global";
import { UnsavedChangesBanner } from "@/components/shared/unsaved-changes-banner";
import { VoiceSlider } from "@/components/shared/voice-slider";
import { Loader } from "@/components/ui/loader";
import { Timer } from "lucide-react";

interface FormErrors {
  role: string;
  persona: string;
  llm_provider: string;
  llm_model: string;
  llm_timeout: string;
  llm_temperature: string;
  llm_max_tokens: string;
}

const toNumberString = (
  value: number | null | undefined,
  defaultValue: string
): string => {
  return value !== null && value !== undefined ? value.toString() : defaultValue;
};

export function AiosAssistantSettings() {
  const queryClient = useQueryClient();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<Record<string, unknown> | null>(null);
  const [previousModel, setPreviousModel] = useState("");

  const [formData, setFormData] = useState({
    role: "",
    persona: "",
    llm_provider: "",
    llm_model: "",
    llm_timeout: "",
    llm_temperature: "",
    llm_max_tokens: "",
  });

  const [errors, setErrors] = useState<FormErrors>({
    role: "",
    persona: "",
    llm_provider: "",
    llm_model: "",
    llm_timeout: "",
    llm_temperature: "",
    llm_max_tokens: "",
  });

  const { data: config, isLoading: isLoadingConfig } = useQuery({
    queryKey: ["aios-assistant-preference"],
    queryFn: () => fetchData("/api/aios-assistant/preference"),
  });

  const { data: llmModelsRaw, isLoading: isLoadingModels } = useQuery({
    queryKey: ["aios-assistant-llm-models"],
    queryFn: () => fetchData("/api/aios-assistant/llm-config/models"),
  });

  const llmModels = useMemo(() => {
    if (!llmModelsRaw) return [];
    if (Array.isArray(llmModelsRaw)) return llmModelsRaw;
    return (llmModelsRaw as { models?: unknown[] }).models ?? [];
  }, [llmModelsRaw]);

  const filteredModels = useMemo(() => {
    if (!formData.llm_provider) return [];
    return llmModels.filter(
      (m: { provider: string }) => m.provider === formData.llm_provider
    );
  }, [llmModels, formData.llm_provider]);

  const selectedModel = useMemo(() => {
    if (!formData.llm_model || !llmModels.length) return null;
    return llmModels.find(
      (m: { model_key: string; model_id: string }) =>
        m.model_key === formData.llm_model || m.model_id === formData.llm_model
    );
  }, [formData.llm_model, llmModels]);

  useEffect(() => {
    if (!config || !llmModels.length) return;

    const agentModel = llmModels.find(
      (m: { model_id: string; model_key: string }) =>
        m.model_id === config.llm_model || m.model_key === config.llm_model
    );
    const provider = config.llm_provider || agentModel?.provider || "OPENAI";
    const normalizedModelKey = agentModel?.model_key || config.llm_model || "";

    const initialData = {
      role: (config as { role?: string }).role ?? "assistant",
      persona: config.persona ?? "",
      llm_provider: provider,
      llm_model: normalizedModelKey,
      llm_timeout: toNumberString(config.llm_timeout, "10"),
      llm_temperature: toNumberString(
        config.llm_temperature,
        (agentModel?.default_temperature ?? 0).toString()
      ),
      llm_max_tokens: toNumberString(
        config.llm_max_tokens,
        (agentModel?.default_max_tokens ?? 0).toString()
      ),
    };

    setFormData(initialData);
    setOriginalFormData(initialData);
    setPreviousModel(normalizedModelKey);
    setHasUnsavedChanges(false);
  }, [config, llmModels]);

  useEffect(() => {
    if (!formData.llm_model || !selectedModel) return;

    if (formData.llm_model !== previousModel) {
      setFormData((prev) => ({
        ...prev,
        llm_max_tokens: (selectedModel as { default_max_tokens: number }).default_max_tokens.toString(),
        llm_temperature: (selectedModel as { default_temperature: number }).default_temperature.toString(),
      }));
      setPreviousModel(formData.llm_model);
      return;
    }

    if (!formData.llm_max_tokens) {
      setFormData((prev) => ({
        ...prev,
        llm_max_tokens: (selectedModel as { default_max_tokens: number }).default_max_tokens.toString(),
      }));
    }

    if (!formData.llm_temperature) {
      setFormData((prev) => ({
        ...prev,
        llm_temperature: (selectedModel as { default_temperature: number }).default_temperature.toString(),
      }));
    }
  }, [formData.llm_model, formData.llm_max_tokens, formData.llm_temperature, previousModel, selectedModel]);

  useEffect(() => {
    if (!originalFormData) return;
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
    setHasUnsavedChanges(hasChanges);
  }, [formData, originalFormData]);

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      patch("/api/aios-assistant/preference", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["aios-assistant-preference"] });
      setOriginalFormData({ ...formData });
      setHasUnsavedChanges(false);
      toast.success("Aios Assistant config updated", {
        description: "Settings have been saved",
      });
    },
    onError: (error: { response?: { data?: { detail?: unknown; message?: string } } }) => {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const newErrors: FormErrors = {
          role: "",
          persona: "",
          llm_provider: "",
          llm_model: "",
          llm_timeout: "",
          llm_temperature: "",
          llm_max_tokens: "",
        };
        detail.forEach((err: { loc?: string[]; msg?: string }) => {
          const field = err.loc?.[1] as keyof FormErrors | undefined;
          if (field && field in newErrors) {
            newErrors[field] = err.msg ?? "";
          }
        });
        setErrors(newErrors);
      } else {
        toast.error("Failed to update config", {
          description:
            (error.response?.data as { message?: string })?.message ??
            "An unexpected error occurred",
        });
      }
    },
  });

  const handleInputChange = (field: string, value: string | number | string[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: Array.isArray(value) ? value : value.toString(),
    }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    setHasUnsavedChanges(true);
  };

  const handleSave = () => {
    if (!originalFormData) return;

    const changedFields: Record<string, unknown> = {};
    const keys = ["role", "persona", "llm_provider", "llm_model", "llm_timeout", "llm_temperature", "llm_max_tokens"] as const;

    keys.forEach((key) => {
      const current = formData[key];
      const original = originalFormData[key];
      const isEqual = current === original;
      if (!isEqual) {
        if (key === "llm_timeout" || key === "llm_max_tokens") {
          changedFields[key] = current ? parseInt(current as string) : null;
        } else if (key === "llm_temperature") {
          changedFields[key] = current ? parseFloat(current as string) : null;
        } else {
          changedFields[key] = current;
        }
      }
    });

    if (formData.llm_model !== originalFormData.llm_model && selectedModel) {
      changedFields.llm_max_tokens = (selectedModel as { default_max_tokens: number }).default_max_tokens;
      const sel = selectedModel as { temperature_min: number; temperature_max: number; default_temperature: number };
      if (sel.temperature_min === sel.temperature_max) {
        changedFields.llm_temperature = sel.default_temperature;
      }
    }

    if (Object.keys(changedFields).length === 0) {
      toast.info("No changes to save");
      return;
    }

    setErrors({
      role: "",
      persona: "",
      llm_provider: "",
      llm_model: "",
      llm_timeout: "",
      llm_temperature: "",
      llm_max_tokens: "",
    });
    updateMutation.mutate(changedFields);
  };

  const handleUndo = () => {
    if (originalFormData) {
      setFormData({ ...originalFormData } as typeof formData);
      setPreviousModel((originalFormData.llm_model as string) ?? "");
      setHasUnsavedChanges(false);
      setErrors({
        role: "",
        persona: "",
        llm_provider: "",
        llm_model: "",
        llm_timeout: "",
        llm_temperature: "",
        llm_max_tokens: "",
      });
      toast.success("Changes reverted");
    }
  };

  const temperatureValue = formData.llm_temperature
    ? parseFloat(formData.llm_temperature)
    : (selectedModel as { default_temperature?: number } | null)?.default_temperature ?? 0;
  const temperatureMin = (selectedModel as { temperature_min?: number } | null)?.temperature_min ?? 0;
  const temperatureMax = (selectedModel as { temperature_max?: number } | null)?.temperature_max ?? 2;
  const isTemperatureFixed =
    selectedModel &&
    (selectedModel as { temperature_min: number }).temperature_min ===
      (selectedModel as { temperature_max: number }).temperature_max;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSave();
      }}
      className="relative h-full flex flex-col"
    >
      {isLoadingConfig && <Loader />}
      <div className="sticky top-[90px] z-10 px-6">
        <UnsavedChangesBanner
          hasUnsavedChanges={hasUnsavedChanges}
          onUndo={handleUndo}
          onSave={handleSave}
          isSaving={updateMutation.isPending}
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
          {/* Behavior Section */}
          <div className="space-y-6">
            <div>
              <h4 className="text-base font-semibold mb-4">Behavior</h4>
              <div className="space-y-6">
                <FormField
                  label="Role"
                  error={errors.role}
                  description="Chat role (e.g. assistant, user, system)"
                >
                  <Input
                    value={formData.role}
                    onChange={(e) => handleInputChange("role", e.target.value)}
                    placeholder="assistant"
                    className={errors.role ? "border-destructive" : ""}
                  />
                </FormField>
                <FormField
                  label="Persona"
                  error={errors.persona}
                  description="Define the agent's personality and expertise"
                >
                  <Textarea
                    value={formData.persona}
                    onChange={(e) => handleInputChange("persona", e.target.value)}
                    placeholder="An expert in super admin tasks and platform support."
                    className={`${TEXTAREA_MIN_HEIGHT} ${errors.persona ? "border-destructive" : ""}`}
                  />
                </FormField>
              </div>
            </div>

            {/* AI Configuration Section */}
            <div className="border-t pt-6">
              <h4 className="text-base font-semibold mb-4">AI Configuration</h4>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="LLM Provider"
                    error={errors.llm_provider}
                    description="Select the AI provider"
                  >
                    <Select
                      value={formData.llm_provider || "OPENAI"}
                      onValueChange={(value) => {
                        handleInputChange("llm_provider", value);
                        const newFiltered = llmModels.filter(
                          (m: { provider: string }) => m.provider === value
                        );
                        handleInputChange(
                          "llm_model",
                          newFiltered[0]?.model_key ?? ""
                        );
                      }}
                      key={`provider-${formData.llm_provider}`}
                    >
                      <SelectTrigger
                        className={`border-border/40 ${errors.llm_provider ? "border-destructive" : ""}`}
                      >
                        <SelectValue placeholder="Select the AI provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="OPENAI">OpenAI</SelectItem>
                        <SelectItem value="ANTHROPIC">Anthropic</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormField>

                  <FormField
                    label="LLM Model"
                    error={errors.llm_model}
                    description="Select the AI model"
                  >
                    <Select
                      value={formData.llm_model || undefined}
                      onValueChange={(value) => handleInputChange("llm_model", value)}
                      key={`model-${formData.llm_provider}-${formData.llm_model || "empty"}`}
                      disabled={isLoadingModels}
                    >
                      <SelectTrigger
                        className={`border-border/40 ${errors.llm_model ? "border-destructive" : ""}`}
                      >
                        <SelectValue
                          placeholder={
                            isLoadingModels ? "Loading models..." : "Select the AI model"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredModels.map((model: { model_id: string; model_key: string; display_name: string }) => (
                          <SelectItem key={model.model_id} value={model.model_key}>
                            {model.display_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormField>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Timer className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-semibold">Temperature</div>
                      <div className="text-xs text-muted-foreground">
                        {selectedModel
                          ? isTemperatureFixed
                            ? "This model has a fixed temperature"
                            : `Controls randomness (${temperatureMin} = deterministic, ${temperatureMax} = very random)`
                          : "Controls randomness in responses (0.0-2.0)"}
                      </div>
                    </div>
                  </div>
                  {selectedModel ? (
                    <VoiceSlider
                      value={[temperatureValue]}
                      onChange={(value) => handleInputChange("llm_temperature", value[0])}
                      min={temperatureMin}
                      max={temperatureMax}
                      step={0.1}
                      unit=""
                      heading=""
                      subheading=""
                      showValue={true}
                    />
                  ) : (
                    <VoiceSlider
                      value={[
                        formData.llm_temperature
                          ? parseFloat(formData.llm_temperature)
                          : 0,
                      ]}
                      onChange={(value) => handleInputChange("llm_temperature", value[0])}
                      min={0}
                      max={2}
                      step={0.1}
                      unit=""
                      heading=""
                      subheading=""
                      showValue={true}
                    />
                  )}
                  {errors.llm_temperature && (
                    <p className="text-sm text-destructive">{errors.llm_temperature}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Timer className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-semibold">Timeout</div>
                      <div className="text-xs text-muted-foreground">
                        Maximum time to wait for AI response (10-300 seconds)
                      </div>
                    </div>
                  </div>
                  <VoiceSlider
                    value={[formData.llm_timeout ? parseInt(formData.llm_timeout) : 10]}
                    onChange={(value) => handleInputChange("llm_timeout", value[0])}
                    min={10}
                    max={300}
                    step={1}
                    unit="sec"
                    heading=""
                    subheading=""
                    showValue={true}
                  />
                  {errors.llm_timeout && (
                    <p className="text-sm text-destructive">{errors.llm_timeout}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Timer className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-semibold">Max Tokens</div>
                      <div className="text-xs text-muted-foreground">
                        {selectedModel
                          ? `Maximum tokens in the response (${(selectedModel as { max_tokens_min: number }).max_tokens_min}-${(selectedModel as { max_tokens_max: number }).max_tokens_max})`
                          : "Maximum tokens in the response (select a model to see limits)"}
                      </div>
                    </div>
                  </div>
                  {selectedModel ? (
                    <VoiceSlider
                      value={[
                        formData.llm_max_tokens
                          ? parseInt(formData.llm_max_tokens)
                          : (selectedModel as { default_max_tokens: number }).default_max_tokens,
                      ]}
                      onChange={(value) => handleInputChange("llm_max_tokens", value[0])}
                      min={(selectedModel as { max_tokens_min: number }).max_tokens_min}
                      max={(selectedModel as { max_tokens_max: number }).max_tokens_max}
                      step={1}
                      unit=""
                      heading=""
                      subheading=""
                      showValue={true}
                    />
                  ) : (
                    <div className="p-3 bg-muted rounded-xl">
                      <p className="text-sm text-muted-foreground text-center">
                        Please select a model to configure max tokens
                      </p>
                    </div>
                  )}
                  {errors.llm_max_tokens && (
                    <p className="text-sm text-destructive">{errors.llm_max_tokens}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Knowledge Bases Section (placeholder, no API) */}
            <div className="border-t pt-6">
              <h4 className="text-base font-semibold mb-4">Knowledge Bases</h4>
              <div className="space-y-6">
                <FormField
                  label="Knowledge Bases"
                  description="Select knowledge bases that contain relevant information for the Aios Assistant."
                >
                  <div className="min-h-[80px] rounded-lg border border-border/40 bg-muted/20 flex items-center justify-center px-4 py-6">
                    <p className="text-sm text-muted-foreground text-center">
                      No knowledge bases selected. Configuration will be available here.
                    </p>
                  </div>
                </FormField>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
